import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classNames from 'classnames';

import type {QuoteDisplayStatus} from 'nutshell-graphql-types';

import type {QuoteTimes} from '../../../../../../ui/lead/sales-documents/shared';

import {getSalesDocumentStatusText} from '../../lead-sidebar/sales-documents/helpers';
import type {SalesDocumentType} from '../../lead-sidebar/sales-documents/types';
import {Body} from '../../typography/body';
import type {Color as BodyColor} from '../../typography/body';
import {useRefObserver} from '../../utils/hooks/use-ref-observer';

import {
    getActiveStatus,
    getStatusLabelIcon,
    getBackgroundComponent,
    getDialogCopy,
    getLabelTooltip,
} from './helpers';
import {StatusDialog} from './status-dialog';

import styles from './status-label.module.css';

/**
 * Supports a subset of the QuoteDisplayStatus types, as not all display
 * statuses are valid status labels.
 */
export type StatusLabelVariant =
    | 'DRAFT'
    | 'PUBLISHED'
    | 'SENT'
    | 'ACCEPTED'
    | 'ARCHIVED'
    | 'REVOKED'
    | 'EXPIRED';

type Props = {
    variant: StatusLabelVariant;
    quoteStatus: QuoteDisplayStatus;
    onAdvance: () => void;
    isDisabled: boolean;
    featureGated?: boolean;
    width?: number;
    height?: number;
    quoteTimes?: QuoteTimes;
    type: SalesDocumentType;
};

export function StatusLabel(props: Props) {
    const [isPopoverShowing, setIsPopoverShowing] = React.useState<boolean>(false);
    const statusLabelRef = React.useRef<HTMLElement | null>(null);
    const {isHovered} = useRefObserver(statusLabelRef, ['isHovered']);

    React.useEffect(() => {
        // Sometimes the tooltip doesn't show up on the first render
        // This is a workaround to force a rebuild
        ReactTooltip.rebuild();
    }, []);

    const handleClick = () => {
        // For invoices, we have another intermediary popover before the status
        // changes to paid so we don't need to show this one
        if (props.type === 'Invoice' && props.variant === 'ACCEPTED') {
            props.onAdvance();
        } else {
            setIsPopoverShowing(!isPopoverShowing);
        }
    };

    const isActive = getActiveStatus(props.variant, props.quoteStatus);
    const BackgroundComponent = getBackgroundComponent(props.variant);
    const copy = getDialogCopy(props.quoteStatus, props.type);

    const backgroundWrapperStyles = classNames({
        [styles['background-wrapper--blue']]: isActive,
        [styles['background-wrapper--green']]:
            props.quoteStatus === 'ACCEPTED' && props.variant === 'ACCEPTED',
        [styles['background-wrapper--grey']]:
            props.quoteStatus === 'ARCHIVED' || props.quoteStatus === 'REVOKED' || !isActive,
        [styles['background-wrapper--red']]: props.quoteStatus === 'EXPIRED',
        [styles['background-wrapper']]: true,
    });

    const statusLabelTextColor = React.useMemo<BodyColor>(() => {
        if (props.quoteStatus === 'ACCEPTED' && props.variant === 'ACCEPTED') {
            return 'green';
        }
        if (props.quoteStatus === 'EXPIRED') {
            return 'alert';
        }
        if (props.quoteStatus === 'REVOKED' || props.quoteStatus === 'ARCHIVED') {
            return 'light';
        }
        if (isActive) {
            return 'person';
        }

        return 'light';
    }, [props.quoteStatus, props.variant, isActive]);

    const statusLabelStyles = classNames({
        [styles['status-label']]: true,
        [styles['status-label--disabled']]: props.isDisabled,
    });

    return (
        <>
            <div
                // @ts-expect-error
                onClick={props.isDisabled ? null : handleClick}
                style={{width: props.width, height: props.height}}
                // @ts-expect-error
                ref={statusLabelRef}
                data-tip={
                    props.quoteTimes
                        ? getLabelTooltip(
                              props.variant,
                              props.quoteStatus,
                              props.quoteTimes,
                              props.featureGated,
                              props.type
                          )
                        : ''
                }
                className={statusLabelStyles}
            >
                <div
                    style={{width: props.width, height: props.height}}
                    className={backgroundWrapperStyles}
                >
                    <BackgroundComponent width={props.width} height={props.height} />
                </div>
                <div className={styles['status-label-content']}>
                    {getStatusLabelIcon(props.variant, isActive, props.type, props.quoteStatus)}
                    <Body
                        size='small'
                        color={statusLabelTextColor}
                        underline={props.isDisabled ? false : isHovered || false}
                    >
                        {getSalesDocumentStatusText(props.variant, props.type)}
                    </Body>
                </div>
            </div>
            {isPopoverShowing && statusLabelRef.current && (
                <StatusDialog
                    anchor={statusLabelRef.current}
                    copy={copy}
                    onPrimary={() => {
                        props.onAdvance();
                        setIsPopoverShowing(false);
                    }}
                    onSecondary={() => setIsPopoverShowing(false)}
                />
            )}
        </>
    );
}
